import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://risqly.net';

let authToken = null;
// Promise that resolves when a token is first set, so first API calls can await it
let authTokenReadyResolve = null;
let authTokenReady = null;

const resetAuthTokenReady = () => {
  authTokenReady = new Promise((resolve) => {
    authTokenReadyResolve = resolve;
  });
};

resetAuthTokenReady();

const createAuthError = (message, code) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

// Set auth token from Auth0
export const setAuthToken = (token) => {
  const normalizedToken = typeof token === 'string' ? token.trim() : '';

  if (!normalizedToken) {
    authToken = null;
    resetAuthTokenReady();
    return;
  }

  authToken = normalizedToken;
  // Unblock any pending calls waiting for the first token
  if (authTokenReadyResolve) {
    authTokenReadyResolve();
    authTokenReadyResolve = null;
  }
};

export const clearAuthToken = () => {
  setAuthToken(null);
};

const waitWithTimeout = async (promise, timeoutMs) => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(createAuthError('Timed out waiting for auth token', 'AUTH_TOKEN_TIMEOUT'));
    }, timeoutMs);
  });

  try {
    await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

// Wait for a token to be set before making protected calls
const waitForAuthToken = async () => {
  if (authToken) return;
  await waitWithTimeout(authTokenReady, 4500);
  if (!authToken) {
    throw createAuthError('Auth token unavailable', 'AUTH_TOKEN_UNAVAILABLE');
  }
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const isRetriableColdStartError = (error) => {
  const status = error?.response?.status;

  if (error?.code === 'ECONNABORTED') {
    return true;
  }

  if (!status) {
    return true;
  }

  return status === 408 || status === 429 || status >= 500;
};

// Add interceptor to include auth token
apiClient.interceptors.request.use((config) => {
  if (authToken && authToken.trim()) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

// ============================================================
// PUBLIC ENDPOINTS
// ============================================================

/**
 * Get total number of companies tracked
 */
export const getCompanyStats = async () => {
  try {
    const response = await apiClient.get('/api/companies/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching company stats:', error);
    throw error;
  }
};

/**
 * Search for companies by name
 */
export const searchCompanies = async (query) => {
  try {
    const response = await apiClient.get('/api/companies/search', {
      params: { q: query },
    });
    return response.data.results || [];
  } catch (error) {
    console.error('Error searching companies:', error);
    throw error;
  }
};

/**
 * Get public company profile
 */
export const getCompanyProfile = async (name, { onRetry, strategy = 'interactive' } = {}) => {
  if (strategy === 'background') {
    try {
      const response = await apiClient.get(`/api/companies/${name}`, {
        timeout: 12000,
      });
      return response.data.company;
    } catch (error) {
      console.error('Error fetching company profile in background mode:', error);
      throw error;
    }
  }

  const firstAttemptTimeoutMs = 1000;
  const retryDelayMs = 2000;
  const retryTimeoutMs = 6000;
  const onRetryCallback = typeof onRetry === 'function' ? onRetry : null;

  try {
    const response = await apiClient.get(`/api/companies/${name}`, {
      timeout: firstAttemptTimeoutMs,
    });
    return response.data.company;
  } catch (error) {
    const shouldRetry = isRetriableColdStartError(error);

    if (!shouldRetry) {
      console.error('Error fetching company profile:', error);
      throw error;
    }

    if (onRetryCallback) {
      onRetryCallback({
        waitMs: retryDelayMs,
        reason: error,
      });
    }

    await sleep(retryDelayMs);

    try {
      const retryResponse = await apiClient.get(`/api/companies/${name}`, {
        timeout: retryTimeoutMs,
      });
      return retryResponse.data.company;
    } catch (retryError) {
      console.error('Error fetching company profile after retry:', retryError);
      throw retryError;
    }
  }
};

/**
 * List companies with optional search and sorting
 */
export const listCompanies = async ({ search = '', orderBy = 'final_score', page = 1, pageSize = 24 } = {}) => {
  try {
    const response = await apiClient.get('/api/companies', {
      params: { search, orderBy, page, pageSize },
    });
    return {
      companies: response.data.companies || [],
      total: response.data.total || 0,
      page: response.data.page || page,
      pageSize: response.data.pageSize || pageSize,
    };
  } catch (error) {
    console.error('Error listing companies:', error);
    throw error;
  }
};

/**
 * Submit a public contact inquiry
 */
export const submitContactInquiry = async ({ name, email, company, subject, message }) => {
  try {
    const response = await apiClient.post('/api/inquiries', {
      name,
      email,
      company,
      subject,
      message,
    });
    return response.data.inquiry;
  } catch (error) {
    console.error('Error submitting contact inquiry:', error);
    throw error;
  }
};

// ============================================================
// PROTECTED ENDPOINTS (Requires Auth0 token)
// ============================================================

/**
 * Get dashboard overview for authenticated user
 */
export const getDashboardOverview = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/dashboard/overview');
    return response.data.overview;
  } catch (error) {
    console.error('Error fetching dashboard overview:', error);
    throw error;
  }
};

export const getSecurityInsightsData = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/dashboard/security-insights');
    return response.data;
  } catch (error) {
    console.error('Error fetching Security Insights:', error);
    throw error;
  }
};

export const getBillingStatus = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/billing/status');
    return response.data.billing;
  } catch (error) {
    console.error('Error fetching billing status:', error);
    throw error;
  }
};

export const createBillingCheckoutSession = async (billingInterval) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/billing/checkout-session', {
      billing_interval: billingInterval,
    });
    return response.data.url;
  } catch (error) {
    console.error('Error creating billing checkout session:', error);
    throw error;
  }
};

export const createBillingPortalSession = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/billing/portal-session', {});
    return response.data.url;
  } catch (error) {
    console.error('Error creating billing portal session:', error);
    throw error;
  }
};

/**
 * Get list of declared vendors
 */
export const getDashboardVendors = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/dashboard/vendors');
    return response.data.vendors || [];
  } catch (error) {
    console.error('Error fetching dashboard vendors:', error);
    throw error;
  }
};

/**
 * Get activity logs
 */
export const getActivityLogs = async (category = null) => {
  try {
    await waitForAuthToken();
    const params = category ? { category } : {};
    const response = await apiClient.get('/api/dashboard/activity', { params });
    return response.data.activities || [];
  } catch (error) {
    console.error('Error fetching activity logs:', error);
    throw error;
  }
};

export const getNotifications = async ({ unreadOnly = false } = {}) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/notifications', {
      params: unreadOnly ? { unread: 'true' } : {},
    });
    return response.data.notifications || [];
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const getUnreadNotificationCount = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/notifications/unread-count');
    return response.data.count || 0;
  } catch (error) {
    console.error('Error fetching unread notification count:', error);
    throw error;
  }
};

export const markNotificationRead = async (notificationId) => {
  await waitForAuthToken();
  await apiClient.patch(`/api/notifications/${notificationId}/read`, {});
};

export const markAllNotificationsRead = async () => {
  await waitForAuthToken();
  await apiClient.post('/api/notifications/read-all', {});
};

/**
 * Get all vendors for management
 */
export const listVendors = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/vendors');
    return response.data.vendors || [];
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
};

/**
 * Add new vendor
 */
export const addVendor = async (vendor_id, importance_weight, contact, name, website_url) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/vendors', {
      vendor_id,
      importance_weight,
      contact,
      name,
      website_url,
    });
    return response.data.vendor;
  } catch (error) {
    console.error('Error adding vendor:', error);
    throw error;
  }
};

/**
 * Update vendor
 */
export const updateVendor = async (id, importance_weight, contact) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.put(`/api/vendors/${id}`, {
      importance_weight,
      contact,
    });
    return response.data.vendor;
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw error;
  }
};

/**
 * Delete vendor
 */
export const deleteVendor = async (id) => {
  try {
    await waitForAuthToken();
    await apiClient.delete(`/api/vendors/${id}`);
    return true;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
};

/**
 * Get network graph visualization data
 */
export const getNetworkGraph = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/vendors/network-graph');
    return response.data;
  } catch (error) {
    console.error('Error fetching network graph:', error);
    throw error;
  }
};

// ============================================================
// AUTH ENDPOINTS
// ============================================================

/**
 * Handle login
 */
export const loginUser = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/auth/login');
    return response.data.user;
  } catch (error) {
    console.error('Error during login:', error);
    // Re-throw with more specific information for access denied cases
    if (error.response?.status === 403 && error.response?.data?.error === 'ACCESS_DENIED') {
      const accessError = new Error('ACCESS_DENIED');
      accessError.code = 'ACCESS_DENIED';
      accessError.companyName = error.response.data.companyName;
      accessError.userMessage = error.response.data.message;
      throw accessError;
    }
    throw error;
  }
};

/**
 * Get current user profile
 */
export const getUserProfile = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

/**
 * Register new company
 */
export const registerCompany = async (companyData) => {
  try {
    const response = await apiClient.post('/api/companies/register', companyData);
    return response.data;
  } catch (error) {
    console.error('Error registering company:', error);
    throw error;
  }
};

// ============================================================
// ONBOARDING ENDPOINTS
// ============================================================

/**
 * Check if company name already exists
 */
export const checkCompanyNameExists = async (normalizedName) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/onboarding/check-company-name', {
      normalized_name: normalizedName,
    });
    return response.data.exists;
  } catch (error) {
    console.error('Error checking company name:', error);
    throw error;
  }
};

/**
 * Get company by domain (for pre-filling onboarding)
 */
export const getCompanyByDomain = async (domain) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get(`/api/onboarding/company-by-domain/${domain}`);
    return {
      company: response.data.company,
      adminEmails: response.data.adminEmails || []
    };
  } catch (error) {
    // Silently fail - this is a non-critical pre-fill operation
    return { company: null, adminEmails: [] };
  }
};

/**
 * Get admin emails for the authenticated user's linked company
 */
export const getMyCompanyAdminEmails = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/onboarding/my-company-admin-emails');
    return response.data.adminEmails || [];
  } catch (error) {
    return [];
  }
};

/**
 * Update company settings and profile fields.
 */
export const updateCompanySettings = async ({
  admin_controlled,
  admin_emails,
  description,
  industry,
  country,
}) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.put('/api/company/settings', {
      admin_controlled,
      admin_emails,
      description,
      industry,
      country,
    });
    return response.data.company;
  } catch (error) {
    console.error('Error updating company settings:', error);
    throw error;
  }
};

/**
 * Save policy documents during onboarding
 * Sends actual PDF files to the server for S3 upload
 */
export const savePolicyDocuments = async (companyId, documents) => {
  try {
    await waitForAuthToken();
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('company_id', companyId);
    
    // Add each file to the FormData
    documents.forEach((doc) => {
      if (doc.file instanceof File) {
        formData.append('documents', doc.file);
      }
    });

    // Create a new request without JSON content-type (let browser set multipart/form-data)
    const response = await axios.post(
      `${API_BASE_URL}/api/onboarding/policy-documents`,
      formData,
      {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : undefined,
          // Don't set Content-Type, let browser handle it for FormData
        },
      }
    );
    
    return response.data.documents;
  } catch (error) {
    console.error('Error saving policy documents:', error);
    throw error;
  }
};

/**
 * Save vendor relationships during onboarding
 */
export const saveVendorRelationships = async (companyId, vendors) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/onboarding/vendors', {
      company_id: companyId,
      vendors,
    });
    return response.data.vendors;
  } catch (error) {
    console.error('Error saving vendor relationships:', error);
    throw error;
  }
};

/**
 * Save a single vendor during onboarding (called immediately when vendor is added)
 */
export const saveSingleVendor = async (companyId, vendor) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/onboarding/vendors', {
      company_id: companyId,
      vendors: [vendor],
    });
    return response.data.vendors[0];
  } catch (error) {
    console.error('Error saving vendor:', error);
    throw error;
  }
};

/**
 * Complete onboarding
 */
export const completeOnboarding = async (companyId, externalScanConsent) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/onboarding/complete', {
      company_id: companyId,
      external_scan_consent: externalScanConsent,
    });
    return response.data.company;
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
};

/**
 * Get policy documents for user's company
 */
export const getPolicyDocuments = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.get('/api/company/policy-documents');
    return response.data.documents;
  } catch (error) {
    console.error('Error fetching policy documents:', error);
    throw error;
  }
};

/**
 * Upload policy documents after onboarding
 */
export const uploadPolicyDocuments = async (documents) => {
  try {
    await waitForAuthToken();
    
    const formData = new FormData();
    
    documents.forEach((doc) => {
      if (doc.file instanceof File) {
        formData.append('documents', doc.file);
      }
    });

    const response = await axios.post(
      `${API_BASE_URL}/api/company/policy-documents`,
      formData,
      {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : undefined,
        },
      }
    );
    
    return response.data.documents;
  } catch (error) {
    console.error('Error uploading policy documents:', error);
    throw error;
  }
};

/**
 * Delete a policy document
 */
export const deletePolicyDocument = async (documentId) => {
  try {
    await waitForAuthToken();
    const response = await apiClient.delete(`/api/company/policy-documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting policy document:', error);
    throw error;
  }
};

/**
 * Download a policy document
 */
export const downloadPolicyDocument = async (s3Key, filename) => {
  try {
    await waitForAuthToken();
    const response = await axios.get(
      `${API_BASE_URL}/api/company/policy-documents/download/${encodeURIComponent(s3Key)}`,
      {
        headers: {
          Authorization: authToken ? `Bearer ${authToken}` : undefined,
        },
        responseType: 'blob',
      }
    );
    
    // Create blob and trigger download
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading policy document:', error);
    throw error;
  }
};

/**
 * Delete the authenticated user's company and associated data
 */
export const deleteCompany = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.delete('/api/company');
    return response.data;
  } catch (error) {
    console.error('Error deleting company:', error);
    throw error;
  }
};

/**
 * Schedule a verification scan for the authenticated user's company.
 */
export const scheduleVerificationScan = async (subpagePath = '') => {
  try {
    await waitForAuthToken();
    const payload = {};

    if (typeof subpagePath === 'string' && subpagePath.trim()) {
      payload.subpagePath = subpagePath.trim();
    }

    const response = await apiClient.post('/api/company/verification-scan', payload);
    return response.data.scan;
  } catch (error) {
    console.error('Error scheduling verification scan:', error);
    throw error;
  }
};

/**
 * Schedule an outside-in scan for the authenticated user's company.
 */
export const scheduleOutsideInScan = async () => {
  try {
    await waitForAuthToken();
    const response = await apiClient.post('/api/company/outside-in-scan', {});
    return response.data.scan;
  } catch (error) {
    console.error('Error scheduling outside-in scan:', error);
    throw error;
  }
};

export default apiClient;
